import { useLayoutEffect } from 'react'
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import { useNoIndex } from './hooks/useNoIndex'

function NoIndex() {
  useNoIndex()
  return null
}
import Header from './components/layout/Header'
import Footer from './components/layout/Footer'
import Home from './pages/Home'
import Catalog from './pages/Catalog'
import Search from './pages/Search'
import Manga from './pages/Manga'
import Reader from './pages/Reader'
import Profile from './pages/Profile'
import Bookmarks from './pages/Bookmarks'
import Admin from './pages/Admin'
import AdminMangaForm from './pages/AdminMangaForm'
import AdminChapterUpload from './pages/AdminChapterUpload'
import AdminUsers from './pages/AdminUsers'
import AdminReports from './pages/AdminReports'
import AdminLookups from './pages/AdminLookups'
import { AdminSupportList, AdminSupportTicketView } from './pages/AdminSupport'
import Login from './pages/Login'
import Register from './pages/Register'
import NotFound from './pages/NotFound'
import Genre from './pages/Genre'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Frozen from './pages/Frozen'
import Banned from './pages/Banned'
import Forbidden from './pages/Forbidden'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import Dmca from './pages/Dmca'
import Contact from './pages/Contact'
import Support from './pages/Support'
import SupportTicket from './pages/SupportTicket'

function ScrollToTop() {
  const { pathname } = useLocation()
  useLayoutEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }) }, [pathname])
  return null
}

function Layout() {
  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <ScrollToTop />
      <Header />
      <main style={{ flex: 1 }}>
        <Outlet />
      </main>
      <Footer />
    </div>
  )
}

function StatusGuard() {
  const user = useAuthStore((s) => s.user)
  const location = useLocation()
  if (user?.status === 'frozen') {
    const ok = location.pathname === '/profile' || location.pathname.startsWith('/support')
    if (ok) return <Outlet />
    return <Navigate to="/profile" replace />
  }
  if (user?.status === 'banned') {
    const reason = encodeURIComponent(user.ban_reason || '')
    return <Navigate to={`/banned?reason=${reason}`} replace />
  }
  return <Outlet />
}

function ProtectedRoute() {
  const isAuth = useAuthStore((s) => s.isAuth)
  const location = useLocation()
  if (!isAuth) {
    const returnUrl = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />
  }
  return <><NoIndex /><Outlet /></>
}

function AdminRoute() {
  const user = useAuthStore((s) => s.user)
  const isAuth = useAuthStore((s) => s.isAuth)
  const location = useLocation()
  if (!isAuth) {
    const returnUrl = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />
  }
  if (user?.role !== 'admin') return <Navigate to="/403" replace />
  return <><NoIndex /><Outlet /></>
}

export const router = createBrowserRouter([
  { path: '/frozen', element: <Frozen /> },
  { path: '/banned', element: <Banned /> },
  {
    element: <StatusGuard />,
    children: [{
      element: <Layout />,
      children: [
      { path: '/', element: <Home /> },
      { path: '/catalog', element: <Catalog /> },
      { path: '/search', element: <Search /> },
      { path: '/manga/:slug', element: <Manga /> },
      { path: '/genre/:slug', element: <Genre /> },
      { path: '/login', element: <Login /> },
      { path: '/register', element: <Register /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/reset-password', element: <ResetPassword /> },
      { path: '/403', element: <Forbidden /> },
      { path: '/terms', element: <Terms /> },
      { path: '/privacy', element: <Privacy /> },
      { path: '/dmca', element: <Dmca /> },
      { path: '/contact', element: <Contact /> },
      {
        element: <ProtectedRoute />,
        children: [
          { path: '/profile', element: <Profile /> },
          { path: '/bookmarks', element: <Bookmarks /> },
          { path: '/support', element: <Support /> },
          { path: '/support/:id', element: <SupportTicket /> },
        ],
      },
      {
        element: <AdminRoute />,
        children: [
          { path: '/admin', element: <Admin /> },
          { path: '/admin/users', element: <AdminUsers /> },
          { path: '/admin/reports', element: <AdminReports /> },
          { path: '/admin/support', element: <AdminSupportList /> },
          { path: '/admin/support/:id', element: <AdminSupportTicketView /> },
          { path: '/admin/lookups', element: <AdminLookups /> },
          { path: '/admin/manga/new', element: <AdminMangaForm /> },
          { path: '/admin/manga/:id', element: <AdminMangaForm /> },
          { path: '/admin/manga/:id/upload', element: <AdminChapterUpload /> },
          { path: '*', element: <NotFound /> },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  }]},
  { path: '/manga/:slug/chapter/:chapterNumber', element: <Reader /> },
])
